import React from 'react';
import {View} from 'react-native';
import {useSelector} from 'react-redux';
import {get} from 'lodash';

import FormattedNumber from '/component/base/FormattedNumber';
import ListItem from '/component/base/ListItem';
import Text from '/component/base/Text';
import ProfileSection from '/component/partial/profile/ProfileSection';
import {DownlineProfile} from '/constant/TestIDs';
import {
  PREMIER_ORDER_STATUS_ACTIVE,
  PREMIER_ORDER_STATUS_IN_ACTIVE,
} from '/enum/premierOrderStatus.enum';
import {withTranslation} from '/provider/TranslationProvider';
import {variablesFeatureFlag} from '/redux/selector/remoteConfig.selector';
import {getDateUtilDefaultInstance} from '/util/date.util';
import {shouldReleaseFeature} from '/util/featureRelease.util';

import {styles} from './ProfileNextPremierOrderSection.style';

interface ProfileNextPremierOrderSectionProps {
  tt: (key: string, value: string) => string;
  orderScheduledOn: string;
  orderCv: string;
  orderQv: string;
  orderTotal: number;
  orderCurrencyCode: string;
  autoShipStatus: number | null;
  isClientProfile: boolean;
}

export const ProfileNextPremierOrderSection = ({
  tt = withTranslation.ttDefaultProp,
  orderScheduledOn = '',
  orderCv = '',
  orderQv = '',
  orderTotal = 0,
  orderCurrencyCode = '',
  autoShipStatus = null,
  isClientProfile = false,
}: ProfileNextPremierOrderSectionProps) => {
  const listItemProps = {
    readonly: true,
    style: {base: styles.listItem},
  };

  const featureFlag = useSelector(variablesFeatureFlag);
  const updatePremierPlus = shouldReleaseFeature(
    get(featureFlag, 'ff_Premier_Plus', ''),
  );

  // Logic for the order title
  const updatedOrderType = updatePremierPlus ? 'Premier+' : 'Premier';

  let rendererListItem = (
    <ListItem
      {...listItemProps}
      label={
        <Text allowFontScaling maxFontSizeMultiplier={2} emphasis="low">
          {tt('noOrder', `No scheduled ${updatedOrderType} Order`)}
        </Text>
      }
    />
  );

  if (
    isClientProfile
      ? orderScheduledOn && autoShipStatus === PREMIER_ORDER_STATUS_ACTIVE
      : orderScheduledOn
  ) {
    rendererListItem = (
      <ListItem
        {...listItemProps}
        label={
          <>
            <Text
              allowFontScaling
              maxFontSizeMultiplier={2}
              emphasis="medium"
              style={styles.nextOrderDate}>
              {getDateUtilDefaultInstance().formatDate(
                orderScheduledOn,
                'MMMM d, yyyy',
              )}
            </Text>
            <Text
              allowFontScaling
              maxFontSizeMultiplier={2}
              testID={DownlineProfile.nextPremierOrder}
              variant="caption"
              emphasis="low">
              {tt('list.cv', 'CV')} {parseFloat(orderCv).toFixed(2)}
              {' · '}
              {tt('list.qv', 'QV')} {parseFloat(orderQv).toFixed(2)}
              {' · '}
              {tt('orderType.premier', `${updatedOrderType}`)}
            </Text>
          </>
        }
        suffix={
          <View style={styles.subtotal}>
            <FormattedNumber
              value={orderTotal}
              currencyCode={orderCurrencyCode}
              decimalPlaces={2}
            />
          </View>
        }
        style={{base: styles.listItem}}
      />
    );
  }

  const shouldRenderProfileSection = isClientProfile
    ? autoShipStatus !== PREMIER_ORDER_STATUS_IN_ACTIVE
    : true;

  return (
    <>
      {shouldRenderProfileSection ? (
        <ProfileSection
          analyticsTitle="Next OPTAVIA Premier Order"
          style={{base: styles.base}}
          title={tt('heading', `Next OPTAVIA ${updatedOrderType} Order`)}>
          {rendererListItem}
        </ProfileSection>
      ) : null}
    </>
  );
};

export default ProfileNextPremierOrderSection;
